import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, DollarSign, TrendingUp, MapPin, Building } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const AsianInvestmentArticle = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-8">
        <Button 
          variant="ghost" 
          onClick={() => window.history.back()}
          className="mb-6 hover:bg-gray-100"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Industry Highlights
        </Button>

        <article className="max-w-4xl mx-auto">
          <header className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <Badge className="bg-green-500 text-white">
                <DollarSign className="h-3 w-3 mr-1" />
                Investment
              </Badge>
              <span className="text-gray-500 text-sm">November 2025</span>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Asian Firms Shift Investment Towards Europe in Supply Chain Realignment
            </h1>
            
            <p className="text-xl text-gray-600 leading-relaxed">
              Chinese foreign direct investment in Europe surges 47% to €10 billion as Asian companies 
              restructure supply chains amid US-China decoupling, with electric vehicles leading the charge.
            </p>
          </header>

          <div className="mb-8">
            <img 
              src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" 
              alt="Asian investment in European business and supply chain infrastructure" 
              className="w-full h-64 object-cover rounded-lg shadow-lg"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <TrendingUp className="h-5 w-5 mr-2 text-green-600" />
                  FDI Growth
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">+47%</div>
                <p className="text-sm text-gray-600">€10 billion total</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Building className="h-5 w-5 mr-2 text-blue-600" />
                  EV Investment
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-600">€4.9B</div>
                <p className="text-sm text-gray-600">83% of greenfield FDI</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <MapPin className="h-5 w-5 mr-2 text-red-600" />
                  US Share
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-red-600">4%</div>
                <p className="text-sm text-gray-600">€2 billion only</p>
              </CardContent>
            </Card>
          </div>

          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Supply Chain Realignment Overview</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              A fundamental shift is occurring in global supply chains as Asian companies, particularly 
              Chinese firms, redirect investments away from the United States toward Europe. This strategic 
              realignment reflects the ongoing US-China decoupling and represents the most significant 
              change in Chinese foreign direct investment patterns since 2016.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Investment Surge Statistics</h3>
            <div className="bg-green-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">2024 Chinese FDI in EU & UK:</h4>
              <ul className="space-y-2">
                <li><strong>Total Investment:</strong> €10 billion (47% increase from 2023)</li>
                <li><strong>Share of Global FDI:</strong> 19.1% (up from 15.4% in 2023)</li>
                <li><strong>First Major Rebound:</strong> Since 2016 investment levels</li>
                <li><strong>US Comparison:</strong> Less than €2 billion (4% of global Chinese FDI)</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Electric Vehicle Investment Dominance</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Electric vehicle projects led the investment surge, accounting for an overwhelming majority of Chinese greenfield FDI:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li><strong>EV Investment Total:</strong> €4.9 billion (83% of all greenfield FDI)</li>
              <li><strong>Greenfield Focus:</strong> Building new operations rather than acquisitions</li>
              <li><strong>Strategic Positioning:</strong> Establishing European manufacturing presence</li>
              <li><strong>Market Access:</strong> Avoiding potential trade barriers</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Major Investment Projects</h3>
            <div className="space-y-4 mb-6">
              <div className="bg-blue-50 p-6 rounded-lg">
                <h4 className="font-semibold text-lg mb-3">Contemporary Amperex Technology (CATL)</h4>
                <ul className="space-y-1">
                  <li><strong>Investment:</strong> €7.3 billion factory in Hungary</li>
                  <li><strong>Status:</strong> Production expected to begin end of 2025</li>
                  <li><strong>Significance:</strong> World's largest EV battery producer</li>
                </ul>
              </div>

              <div className="bg-purple-50 p-6 rounded-lg">
                <h4 className="font-semibold text-lg mb-3">BYD Manufacturing</h4>
                <ul className="space-y-1">
                  <li><strong>Hungary Factory:</strong> First EU facility, production starting next year</li>
                  <li><strong>Turkey Plant:</strong> Additional facility leveraging EU customs union</li>
                  <li><strong>Market Position:</strong> World's largest EV maker</li>
                </ul>
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Strategic Drivers</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              According to ING's Uday Sareen, CEO and head of wholesale banking for Asia-Pacific:
            </p>
            <blockquote className="border-l-4 border-green-500 pl-6 italic text-gray-700 mb-6">
              "We see a structural change in supply chain realignment. The US tariff scenario is a 
              significant component of costs for manufacturers and the supply chain."
            </blockquote>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Diversification Imperative</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              Companies across the Asia-Pacific region recognize the need to diversify their supply chains 
              as Washington continues trade negotiations. This diversification strategy aims to reduce 
              dependency on any single market and mitigate risks associated with trade tensions and 
              potential tariff increases.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Sector Analysis</h3>
            <div className="bg-gray-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">Investment Distribution by Sector:</h4>
              <ul className="space-y-2">
                <li><strong>Electric Vehicles:</strong> Dominant sector with €4.9B investment</li>
                <li><strong>Consumer Electronics:</strong> Significant but smaller allocation</li>
                <li><strong>Appliances:</strong> Manufacturing and assembly operations</li>
                <li><strong>Battery Technology:</strong> Supporting EV ecosystem development</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Geographic Preferences</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              Hungary emerges as a preferred destination for Chinese investment, particularly in the 
              automotive sector. The country's strategic location, skilled workforce, and favorable 
              investment climate make it attractive for companies seeking European market access while 
              maintaining competitive manufacturing costs.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Market Implications</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              This investment shift has profound implications for global supply chains. European markets 
              benefit from increased manufacturing capacity and job creation, while Asian companies gain 
              strategic positioning closer to key consumer markets. The trend also reflects the growing 
              importance of regional supply chain resilience over global efficiency optimization.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Future Outlook</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              The structural nature of this supply chain realignment suggests it will continue beyond 
              current trade tensions. Companies are building long-term strategies that prioritize 
              diversification and regional presence, fundamentally reshaping global manufacturing and 
              logistics networks.
            </p>

            <div className="bg-gray-100 p-6 rounded-lg">
              <h4 className="font-semibold mb-2">Source Information:</h4>
              <p className="text-sm text-gray-600">
                Based on Rhodium Group research and ING banking analysis reported by South China Morning Post. 
                <a href="https://www.scmp.com/business/banking-finance/article/3327182/asian-firms-shift-investment-towards-europe-supply-chain-realignment-ing-says" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline ml-1">
                  View original source
                </a>
              </p>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default AsianInvestmentArticle;